// Payouts → Eligibility — every day's verdict, with its numbers attached.
//
// One immutable row per (device, IST day): the trips and hours the day
// actually produced, the gate it was judged against, and the verdict. This
// is where "why did that day pay zero" answers itself — each below-bar
// number is shown in red against the bar it missed.
//
// THE DIAL at the top is the decision tool: replay any candidate gate over
// the frozen history and see what share of the fleets' money would have
// paid, BEFORE committing anything. (Committing = a new premise-operator
// version on Agreements & gates, effective next month.) The presets are the
// gates actually debated on 3 Sep, with their measured outcomes.

import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { PageHead, DataTable, Pill, Loading, ErrorState, Empty, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { inr, fmtDay, BASE, shaped } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PRESETS = [
  { id: 'current', label: 'Gate to Sep · 4 trips + 5 driving-h', q: { min_trips: 4, min_driving_hours: 5 } },
  { id: 'next', label: 'Gate from Oct · 4 trips + 4 pax-h', q: { min_trips: 4, min_presence_hours: 4 } },
  { id: 'pax2', label: '4 trips + 2 passenger-h', q: { min_trips: 4, min_presence_hours: 2 } },
  { id: 'pax4', label: '4 trips + 4 passenger-h', q: { min_trips: 4, min_presence_hours: 4 } },
  { id: 'custom', label: 'Custom…', q: null },
];

function Dial() {
  const [preset, setPreset] = useState('current');
  const [custom, setCustom] = useState({ min_trips: '4', min_driving_hours: '', min_presence_hours: '' });
  const [out, setOut] = useState(null);
  const [error, setError] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const params = useMemo(() => {
    if (preset !== 'custom') return PRESETS.find((p) => p.id === preset).q;
    const q = {};
    for (const k of Object.keys(custom)) if (String(custom[k]).trim() !== '') q[k] = custom[k];
    return q;
  }, [preset, custom]);

  const run = async () => {
    setError(null);
    try {
      const [r, b] = await Promise.all([
        api.get(`${BASE}/preview/gate`, { params }),
        baseline ? Promise.resolve({ data: baseline }) : api.get(`${BASE}/preview/gate`, { params: PRESETS[0].q }),
      ]);
      setOut(r.data);
      setBaseline(b.data);
    } catch (e) {
      setError(e?.response?.data?.details || e?.response?.data?.error || e.message);
    }
  };

  const delta = out && baseline && out.passing_paise != null
    ? Number(out.passing_paise) - Number(baseline.passing_paise) : null;

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>The dial — try a gate on history before committing it</div>
      <div className={s.panelSub}>
        Replays the candidate over frozen days and the money those days actually carried —
        born-priced days only, 1 Sep 2026 onward (the first month every line was priced under the
        presence regime; August was priced under the earlier rules and is excluded from the pool).
        Nothing moves: committing a gate is a new fleet term on <NavLink to="/v2/payouts/agreements">Agreements &amp; gates</NavLink>, effective next month.
      </div>
      <div className={s.presets}>
        {PRESETS.map((p) => (
          <button key={p.id} type="button"
            className={`${s.preset} ${preset === p.id ? s.presetOn : ''}`}
            onClick={() => setPreset(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className={s.formRow}>
          <div className={s.field}>
            <label>Min trips / day</label>
            <input type="number" value={custom.min_trips} onChange={(e) => setCustom({ ...custom, min_trips: e.target.value })} />
          </div>
          <div className={s.field}>
            <label>Min driving hours</label>
            <input type="number" step="0.5" value={custom.min_driving_hours} onChange={(e) => setCustom({ ...custom, min_driving_hours: e.target.value })} />
          </div>
          <div className={s.field}>
            <label>Min passenger hours</label>
            <input type="number" step="0.5" value={custom.min_presence_hours} onChange={(e) => setCustom({ ...custom, min_presence_hours: e.target.value })} />
          </div>
        </div>
      )}
      <div className={s.formRow} style={{ marginBottom: 0 }}>
        <Button primary onClick={run}>Preview this gate</Button>
      </div>
      {error && <div className={s.badBanner}>{error}</div>}
      {out && (
        <div className={s.dialResult}>
          <span className={s.dialPct}>{out.passing_pct == null ? '—' : `${out.passing_pct}%`}</span>
          <span className={s.dialSub}>
            of fleet money passes · <b>{inr(out.passing_paise)}</b> pays, <b>{inr(Number(out.media_pool_paise) - Number(out.passing_paise))}</b> gates
            {delta != null && delta !== 0 && (
              <> · {delta > 0 ? '+' : '−'}{inr(Math.abs(delta))} vs the current gate</>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

// value / needed, colored by whether the bar was met
function VsBar({ value, needed, unit = '' }) {
  const num = Number(value);
  if (needed == null) return <span>{num.toLocaleString('en-IN')}{unit}</span>;
  const ok = num >= needed;
  return (
    <span className={ok ? s.met : s.unmet}>
      {num.toLocaleString('en-IN')}{unit} <span className={s.needed}>/ {needed}{unit}</span>
    </span>
  );
}

export default function Eligibility() {
  const [range, setRange] = useState({ from: daysAgo(14), to: daysAgo(1), device: '', gatedOnly: false });
  const subjects = useApi(() => api.get(`${BASE}/subjects`).then((r) => shaped(r.data, 'tenants')), []);
  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/eligibility`, {
      params: { from: range.from, to: range.to, ...(range.device ? { device_id: range.device } : {}) },
    }).then((r) => shaped(r.data, 'days')),
    [range.from, range.to, range.device]);

  // A screen's human name is the PLATE of the cab it rides; billing only
  // knows thing names, so resolve plates through the fleet registry's batched
  // lookup (hardware bridge → dated vehicle link), 200 ids per request so the
  // URL stays sane at the 3K-device design point. Plate-less devices keep
  // their thing_name, and the verdicts never wait on this lookup.
  const [plates, setPlates] = useState({});
  const plateKey = useMemo(() => {
    const ids = new Set();
    for (const d of subjects.data?.devices || []) if (d.id) ids.add(d.id);
    for (const r of data?.days || []) if (r.device_id) ids.add(r.device_id);
    return [...ids].sort().join(',');
  }, [subjects.data, data]);
  useEffect(() => {
    if (!plateKey) return undefined;
    let live = true;
    const ids = plateKey.split(',');
    const chunks = [];
    for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
    Promise.all(chunks.map((c) => api.get('/v2/onboarding/screen-plates', { params: { ids: c.join(',') } })
      .then((r) => r.data?.plates || {}).catch(() => ({}))))
      .then((maps) => { if (live) setPlates((p) => Object.assign({}, p, ...maps)); });
    return () => { live = false; };
  }, [plateKey]);

  const head = (
    <PageHead
      title="Day eligibility"
      sub="One immutable verdict per screen per IST day — the trips and hours it produced, against the gate that judged them. Why a day paid zero answers itself here."
      actions={<Button onClick={reload}>Refresh</Button>}
    />
  );

  if (loading) return <div className={s.wrap}>{head}<Loading what="the day verdicts" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reload} /></div>;

  let rows = (data?.days || []).map((r) => ({ ...r, plate: plates[r.device_id] || null }));
  if (range.gatedOnly) rows = rows.filter((r) => !r.eligible);
  const eligibleN = (data?.days || []).filter((r) => r.eligible).length;

  const columns = [
    { key: 'ist_day', label: 'Day', render: (r) => fmtDay(r.ist_day) },
    { key: 'thing_name', label: 'Screen', sort: (r) => r.plate || r.thing_name || '',
      title: 'The screen, by the cab it rides — the device thing-name beneath',
      render: (r) => {
        const thing = r.thing_name || r.device_id?.slice(0, 8);
        return r.plate
          ? <>{r.plate}<span className={s.mono} style={{ display: 'block', fontSize: 11, color: 'var(--ink-faint)' }}>{thing}</span></>
          : <span className={s.mono}>{thing}</span>;
      } },
    { key: 'trips', label: 'Trips', align: 'num', sort: (r) => r.trips,
      title: 'Passenger trips that IST day, from the signals plane',
      render: (r) => <VsBar value={r.trips} needed={r.gate_profile?.min_trips} /> },
    { key: 'driving_hours', label: 'Driving', align: 'num', sort: (r) => Number(r.driving_hours),
      title: 'Hours the cab was actually moving — the workedness signal the gate uses',
      render: (r) => <VsBar value={r.driving_hours} needed={r.gate_profile?.min_driving_hours} unit="h" /> },
    { key: 'presence_hours', label: 'Passengers', align: 'num', sort: (r) => Number(r.presence_hours),
      title: 'Measured passenger-presence hours. Per-play presence verification prices every billed play only from 1 Sep 2026 (born-priced); August days were billed under the earlier trips + driving-hours regime, where a play with nobody aboard could still bill',
      render: (r) => <VsBar value={r.presence_hours} needed={r.gate_profile?.min_presence_hours} unit="h" /> },
    { key: 'eligible', label: 'Verdict', sort: (r) => (r.eligible ? 1 : 0),
      render: (r) => <Pill tone={r.eligible ? 'grow' : 'bad'}>{r.eligible ? 'pays' : 'gated'}</Pill> },
  ];

  return (
    <div className={s.wrap}>
      {head}
      <Dial />
      <div className={s.formRow}>
        <div className={s.field}>
          <label>From</label>
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </div>
        <div className={s.field}>
          <label>To</label>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </div>
        <div className={`${s.field} ${s.fieldWide}`}>
          <label>Screen</label>
          <select value={range.device} onChange={(e) => setRange({ ...range, device: e.target.value })}>
            <option value="">All screens</option>
            {(subjects.data?.devices || []).map((d) => (
              <option key={d.id} value={d.id}>{plates[d.id] ? `${plates[d.id]} — ${d.thing_name}` : d.thing_name}</option>
            ))}
          </select>
        </div>
        <div className={s.field}>
          <label>&nbsp;</label>
          <Button onClick={() => setRange({ ...range, gatedOnly: !range.gatedOnly })}>
            {range.gatedOnly ? 'Showing gated days only' : 'Show gated days only'}
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <Empty title="No frozen days in this range">
          Yesterday freezes each morning (missed days catch up on their own). Widen the range, or run
          “Freeze yesterday” on the Payouts screen.
        </Empty>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          getKey={(r) => `${r.ist_day}|${r.device_id}`}
          filterOn={['ist_day', 'thing_name', 'plate']}
          filterPlaceholder="Filter by day, plate or screen…"
          initialSort={{ key: 'ist_day', dir: 'desc' }}
          right={<span className={s.hint}>{eligibleN} of {(data?.days || []).length} days pay in this range</span>}
        />
      )}
      <div style={{ height: 14 }} />
      <Explainer terms={['eligibility', 'gate', 'earned', 'settled']} />
    </div>
  );
}
